import { useCallback, useEffect, useState } from "react";
import { KeyRound, Loader2, Plus, Trash2 } from "lucide-react";
import { useAuth } from "../../auth/AuthContext";
import { Card } from "./_shared";
import { InviteUserModal, ResetPasswordModal } from "./UserModals";

export function UsersTab({ onChange }) {
  const { apiFetch, user: me } = useAuth();
  const [items, setItems] = useState(null);
  const [showInvite, setShowInvite] = useState(false);
  const [busy, setBusy] = useState({});
  const [resetting, setResetting] = useState(null);

  const load = useCallback(async () => {
    setItems(null);
    try {
      const d = await apiFetch("/api/auth/users");
      setItems(d.items || []);
    } catch { setItems([]); }
  }, [apiFetch]);

  useEffect(() => { load(); }, [load]);

  const removeUser = async (u) => {
    if (!confirm(`Remove admin ${u.email}?`)) return;
    setBusy((b) => ({ ...b, [u.id]: true }));
    try {
      await apiFetch(`/api/auth/users/${encodeURIComponent(u.id)}`, { method: "DELETE" });
      load(); onChange?.();
    } catch (e) { alert(e.message); }
    finally { setBusy((b) => ({ ...b, [u.id]: false })); }
  };

  return (
    <div className="space-y-px">
      <Card title="ADMIN USERS"
            subtitle="Owner-only. Invite admins by email + temporary password. They change their own password after first login."
            accent="#22D3EE" testid="users-card">

        <div className="flex items-center justify-between mb-5">
          <div className="text-[10px] tracking-[0.25em] text-[var(--srs-ink-faint)] font-bold">
            {items?.length ?? "—"} account{items?.length === 1 ? "" : "s"}
          </div>
          <button onClick={() => setShowInvite(true)}
            data-testid="invite-open-btn"
            className="inline-flex items-center gap-2 px-4 py-2 bg-[#22D3EE] hover:bg-[#1abacf] text-black text-[10px] tracking-[0.2em] font-bold uppercase transition-colors">
            <Plus className="w-3.5 h-3.5" /> Invite admin
          </button>
        </div>

        {items === null && (
          <div className="text-xs text-[var(--srs-ink-faint)] flex items-center gap-2 py-8 justify-center">
            <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…
          </div>
        )}

        <div className="flex flex-col gap-2" data-testid="users-list">
          {items?.map((u) => {
            const isMe = u.email === me?.email;
            const isOwner = u.role === "owner";
            return (
              <div key={u.id} className="border border-[var(--srs-line)] bg-[var(--srs-surface-2)] p-4 flex flex-wrap items-center justify-between gap-3" data-testid={`user-row-${u.email}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-sm text-[var(--srs-ink)] truncate">{u.email}</span>
                    <span
                      className="px-2 py-0.5 text-[9px] tracking-[0.2em] font-bold uppercase"
                      style={{
                        background: isOwner ? "rgba(34,211,238,0.12)" : "rgba(16,185,129,0.10)",
                        color: isOwner ? "#22D3EE" : "#10B981",
                        border: `1px solid ${isOwner ? "rgba(34,211,238,0.35)" : "rgba(16,185,129,0.30)"}`,
                      }}>
                      {u.role}
                    </span>
                    {isMe && (
                      <span className="px-2 py-0.5 text-[9px] tracking-[0.2em] font-bold uppercase bg-[var(--srs-surface-2)] text-[var(--srs-ink-dim)] border border-[var(--srs-line)]">YOU</span>
                    )}
                  </div>
                  <div className="mt-1 text-[10px] text-[var(--srs-ink-faint)] tracking-wide flex flex-wrap gap-3">
                    {u.name && <span>{u.name}</span>}
                    {u.created_at && <span>added {new Date(u.created_at).toLocaleDateString()}</span>}
                    {u.created_by && u.created_by !== "system" && <span>by {u.created_by}</span>}
                    {u.last_login && <span>last login {new Date(u.last_login).toLocaleDateString()}</span>}
                  </div>
                </div>
                <div className="flex gap-2">
                  {!isOwner && (
                    <button onClick={() => setResetting(u)}
                      data-testid={`user-reset-${u.email}`}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] tracking-[0.2em] font-bold uppercase border border-[var(--srs-line)] hover:border-[#22D3EE] hover:text-[#22D3EE] text-[var(--srs-ink-dim)] transition-colors">
                      <KeyRound className="w-3 h-3" /> Reset pw
                    </button>
                  )}
                  {!isOwner && !isMe && (
                    <button onClick={() => removeUser(u)} disabled={busy[u.id]}
                      data-testid={`user-delete-${u.email}`}
                      className="inline-flex items-center justify-center w-8 h-8 border border-[var(--srs-line)] hover:border-[#EF4444] hover:text-[#EF4444] text-[var(--srs-ink-dim)] transition-colors disabled:opacity-40">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </Card>

      {showInvite && (
        <InviteUserModal onClose={() => setShowInvite(false)} onCreated={() => { setShowInvite(false); load(); }} />
      )}
      {resetting && (
        <ResetPasswordModal user={resetting} onClose={() => setResetting(null)} onDone={() => { setResetting(null); load(); }} />
      )}
    </div>
  );
}


